/* The plain words this console uses for device speed limits: what a device's
 * limit is, where it comes from, and whether the appliance has it in place.
 * The Device Speed Limits screen and the device drawer both read from here so
 * they cannot drift apart. The appliance decides; these words only say it. */

import { fmtBitRate } from "../utils/formatters";

export const TITLE = "Device Speed Limits";

/* The edge's own bounds (pkg/devicectl MinCapMbit, MaxCapMbit) arrive with the
   view; these are what a form falls back to before it has one. */
export const CAP_FLOOR_MBIT = 1;
export const CAP_CEILING_MBIT = 10000;

// Matches maxLabelLen in cloud-anchor/pkg/api/wanlabels.go.
export const MAX_PROFILE_NAME = 32;

/* A limit as it is said: Mbit/s, and Gbit/s from a thousand of them. */
export const rate = (mbit) => fmtBitRate(Number(mbit), { style: "bit/s" });

export function capped(profile) {
  return !!profile && (profile.down_mbit > 0 || profile.up_mbit > 0);
}

/* What a profile limits, first, as a person would say it. */
export function limitHeadline(profile) {
  if (!capped(profile)) return "No limit";
  const down = profile.down_mbit > 0 ? `${rate(profile.down_mbit)} down` : "";
  const up = profile.up_mbit > 0 ? `${rate(profile.up_mbit)} up` : "";
  return `Up to ${[down, up].filter(Boolean).join(", ")}`;
}

/* Which internet lines a profile allows. `wanName` gives a line the name the
   operator gave it. Empty when the profile leaves lines to the router. */
export function linesWords(profile, wanName) {
  const lines = (profile?.circuits || []).map(wanName);
  if (lines.length === 0) return "";
  return lines.length === 1 ? `${lines[0]} only` : `Only ${lines.slice(0, -1).join(", ")} and ${lines[lines.length - 1]}`;
}

/* The bonding choices, as the form offers them and as a profile reads. */
export const BONDING_CHOICES = [
  ["", "Router default"],
  ["off", "Never combine lines"],
  ["on", "Always combine lines"],
];

export function bondingWords(profile) {
  if (profile?.bonding === "off") return "Lines never combined";
  if (profile?.bonding === "on") return "Lines always combined";
  return "";
}

/* Where a profile's devices may connect, set on the Firewall screen. Empty
   when they may connect anywhere. */
export function destinationsWords(profile) {
  const n = (profile?.destinations || []).length;
  if (!n) return "";
  return n === 1 ? "Reaches one place only (Firewall)" : `Reaches ${n} places only (Firewall)`;
}

/* Everything a profile does, in one sentence: its limit, its lines, its
   bonding and where its devices may connect, leaving out whatever it leaves
   to the router. */
export function profileSummary(profile, wanName) {
  return [limitHeadline(profile), linesWords(profile, wanName), bondingWords(profile), destinationsWords(profile)]
    .filter(Boolean).join(". ");
}

/* One kind of traffic doing something different from the rest of a profile's.
   The appliance keys these by class id ("REALTIME", "APP_ZOOM"); `label` is the
   console's own name for that class. */
export const CLASSES = {
  title: "Traffic that uses different lines",
  none: "Everything this profile's devices send uses the lines above.",
  add: "Add a kind of traffic…",
  within: "A kind of traffic can only use lines this profile already allows.",
};

export function classWords(label, row, wanName) {
  if (row?.block) return `${label}: blocked (Firewall)`;
  const parts = [linesWords(row, wanName), bondingWords(row)].filter(Boolean);
  return parts.length ? `${label}: ${parts.join(". ")}` : label;
}

/* Each of a profile's exceptions, as sentences. `labelFor` names a class id. */
export function classSummaries(profile, wanName, labelFor) {
  return Object.entries(profile?.classes || {}).map(([id, row]) => classWords(labelFor(id), row, wanName));
}

/* How many kinds of traffic a profile treats differently. The drawer says only
   this much: naming them needs the console's class labels, which live with the
   screen that edits them. */
export function classCountWords(profile) {
  const rows = Object.values(profile?.classes || {});
  const blocked = rows.filter((r) => r.block).length;
  const lines = rows.length - blocked;
  return [
    lines === 1 ? "One kind of traffic uses different lines." : lines ? `${lines} kinds of traffic use different lines.` : "",
    blocked === 1 ? "One kind of traffic is blocked." : blocked ? `${blocked} kinds of traffic are blocked.` : "",
  ].filter(Boolean).join(" ");
}

/* A profile's class rows split by who edits them: the lines a kind of traffic
   uses here, what it blocks on the Firewall screen. */
export const lineClasses = (classes) =>
  Object.fromEntries(Object.entries(classes || {}).filter(([, row]) => !row.block));
export const blockedClasses = (classes) =>
  Object.fromEntries(Object.entries(classes || {}).filter(([, row]) => row.block));

/* A class row an operator added but never filled in changes nothing, and the
   appliance refuses it, so it is not sent. */
export function filledClasses(classes) {
  const out = {};
  for (const [id, row] of Object.entries(classes || {})) {
    if (row.bonding || (row.circuits || []).length) out[id] = row;
  }
  return out;
}

export const LINES = {
  title: "Internet lines it may use",
  all: "Every line the router uses",
  permission: "A device held to lines that are all down goes offline rather than using another line.",
  direct: "Lines and combining apply only while this box runs through the Weavian cloud; here they are kept but not used.",
};

/* What a device sent on each line, and what it could not send because none of
   its lines was up, from the appliance's own counters. */
export function usageWords(traffic, wanName, fmtBytes) {
  if (!traffic) return "";
  const lines = Object.entries(traffic.circuits || {}).map(([iface, c]) => `${wanName(iface)} ${fmtBytes(c.bytes)}`);
  const held = traffic.no_circuit?.packets
    ? `; ${traffic.no_circuit.packets} packets held back while none of its lines was up`
    : "";
  return lines.length ? `Sent on ${lines.join(", ")}${held}.` : "";
}

/* Where one device's limit comes from. `joined` is a device in no profile of
   its own that took the one new devices join. */
export function limitSource(profile, joined) {
  if (!profile) return "This device uses the router's settings.";
  if (joined) return `Set by ${profile.name}, the profile new devices join.`;
  if (!capped(profile)) return `In ${profile.name}, which sets no speed limit.`;
  return `Set by the ${profile.name} profile.`;
}

/* Each limit applies to every device in the profile separately. Said once,
   wherever a limit is edited, because the other reading, a total shared by the
   group, is the one people assume. */
export const PER_DEVICE = "Each device in the profile gets this limit on its own; it is not shared.";

export const NO_MAC =
  "This device's hardware address is not known yet, so it cannot be given a limit. " +
  "It appears within a minute of the device sending anything.";

export const NEW_DEVICES = {
  title: "New devices",
  none: "Use the router's settings (no limit)",
  explain:
    "A device you have not put in a profile joins this one while it is on the network. " +
    "A guest's phone joins the first time it connects, and so does a phone that changes " +
    "its private address, since it looks new each time.",
};

/* Whether the appliance has a device's limit in place, from its own reading of
   the kernel rather than from what was asked. */
export function enforcementWords(entry) {
  if (!entry) return { ok: false, label: "Not in place yet", detail: "The appliance has not applied this limit yet. It checks every few seconds." };
  if (entry.enforced) return { ok: true, label: "In place", detail: "" };
  return { ok: false, label: "Not in place", detail: "The appliance could not apply this limit. It keeps trying." };
}

/* What the device's queue did to hold its limit: packets dropped, and marked
   instead where the device's connection accepts marks. */
export function heldBy(counters) {
  const marked = counters.marks ? ` and ${counters.marks} marked` : "";
  return `${counters.drops} packets dropped${marked} to hold it`;
}

/* A profile name for one device's own limits: its name, made unique. */
export function ownProfileName(deviceName, profiles) {
  const taken = new Set((profiles || []).map((p) => p.name.toLowerCase()));
  const base = (deviceName || "This device").slice(0, MAX_PROFILE_NAME);
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const suffix = ` ${n}`;
    const name = base.slice(0, MAX_PROFILE_NAME - suffix.length) + suffix;
    if (!taken.has(name.toLowerCase())) return name;
  }
}

export function normalizeMac(mac) {
  return (mac || "").trim().toLowerCase().replace(/-/g, ":");
}
